import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { ConfiguredStepState, TourItemStateDto, TourResponse, TourState } from '../models/tour.model';
import { TourMaster, TourStepConfig } from '../models/tour-admin.model';
import { TourApiService } from './tour-api.service';
import { GuideService, GuideStep } from 'src/app/services/guide.service';
import { StepGuideService } from 'src/app/services/step-guide.service';
import { Constant } from 'src/app/constant/constant';

const STORAGE_KEY = 'calsoft_tour_step_index';

@Injectable({ providedIn: 'root' })
export class TourService {

  private readonly _state$ = new BehaviorSubject<TourState>({
    active: false,
    loading: false,
    error: null,
    data: null,
    currentStepIndex: 0,
  });

  private readonly anchors = new Map<string, ElementRef>();

  /** Full reactive state */
  readonly state$ = this._state$.asObservable();

  /** Whether the tour overlay is visible */
  readonly tourActive$ = this.state$.pipe(map(s => s.active));

  /** Loading indicator */
  readonly loading$ = this.state$.pipe(map(s => s.loading));

  /** Error message, if any */
  readonly error$ = this.state$.pipe(map(s => s.error));

  /** Flat ordered list of steps */
  readonly steps$ = this.state$.pipe(map(s => s.data?.items ?? []));

  /** Currently active step */
  readonly currentStep$ = this.state$.pipe(
    map(s => (s.data?.items ?? [])[s.currentStepIndex] ?? null),
  );

  /** Index of the currently active step */
  readonly currentStepIndex$ = this.state$.pipe(map(s => s.currentStepIndex));

  /** Tour summary (progress totals) */
  readonly summary$ = this.state$.pipe(map(s => s.data?.checklistSummary ?? null));

  constructor(
    private readonly tourApi: TourApiService,
    private readonly router: Router,
    private readonly guideService: GuideService,
    private readonly stepGuideService: StepGuideService,
  ) { }

  // ─────────────────────────────────────────────────────────────────────
  // Anchor registration
  // ─────────────────────────────────────────────────────────────────────

  registerAnchor(id: string, ref: ElementRef): void {
    this.anchors.set(id, ref);
  }

  unregisterAnchor(id: string): void {
    this.anchors.delete(id);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Tour lifecycle
  // ─────────────────────────────────────────────────────────────────────

  /** Fetch tour data from the backend and open the overlay. Idempotent. */
  startTour(): void {
    const current = this._state$.value;
    if (current.active || current.loading) {
      return;
    }
    this.patch({ loading: true, error: null });

    this.tourApi.startTour().subscribe({
      next: (response: TourResponse) => {
        if (!response.tourEnabled) {
          this.patch({
            loading: false,
            error: 'No tour is configured for your current plan.',
            active: false,
          });
          return;
        }

        const items = response.items ?? [];
        const startIndex = this.resolveStartIndex(items);

        document.body.classList.add('tour-active');
        this.patch({
          loading: false,
          active: true,
          data: response,
          currentStepIndex: startIndex,
          error: null,
        });
        this.afterStepChange();
      },
      error: err => {
        this.patch({
          loading: false,
          error: err?.error?.statusMessage || err?.error?.status_message || 'Failed to start tour.',
        });
      },
    });
  }

  /** Jump to a specific step by index. */
  selectStep(index: number): void {
    const items = this._state$.value.data?.items ?? [];
    if (index < 0 || index >= items.length) {
      return;
    }
    this.patch({ currentStepIndex: index });
    sessionStorage.setItem(STORAGE_KEY, String(index));
    this.afterStepChange();
  }

  /** Advance to the next step. */
  nextStep(): void {
    const state = this._state$.value;
    const total = state.data?.items?.length ?? 0;
    if (state.currentStepIndex < total - 1) {
      this.selectStep(state.currentStepIndex + 1);
    }
  }

  /** Go back to the previous step. */
  prevStep(): void {
    const state = this._state$.value;
    if (state.currentStepIndex > 0) {
      this.selectStep(state.currentStepIndex - 1);
    }
  }

  /** Mark current step as COMPLETED and advance. */
  completeStep(): void {
    const item = this.currentItem;
    if (!item) {
      return;
    }
    this.mutateItemStatus(item, 'COMPLETED');
    item.completedAt = new Date().toISOString();
    this.tourApi.markCompleted(item.stateId).subscribe();
    this.refreshSummary();
    if (item.routePath) {
      this.router.navigateByUrl(item.routePath);
    }
    setTimeout(() => this.nextStep(), 300);
  }

  /** Mark current step as SKIPPED and advance. */
  skipStep(): void {
    const item = this.currentItem;
    if (!item) {
      return;
    }
    this.mutateItemStatus(item, 'SKIPPED');
    this.tourApi.markSkipped(item.stateId).subscribe();
    this.refreshSummary();
    setTimeout(() => this.nextStep(), 300);
  }

  /**
   * Launch the linked StepGuide walkthrough for the current checklist item.
   * When the guide finishes, the item is marked COMPLETED and the tour state
   * is refreshed from the backend.
   */
  playGuideForCurrentItem(): void {

    const item = this.currentItem;


    if (!item) {
      // No guide linked — just mark done manually
      this.completeStep();
      return;
    }

    let stepGuide = 0;

    let deviceTypeLong = Constant.DEVICE_MODEL

    if (deviceTypeLong != undefined && deviceTypeLong == "1") {
      if (window.innerWidth > 768) {
        deviceTypeLong = "1";

        if (item.linkedStepGuideId <= 0) {
          // No guide linked — just mark done manually
          this.completeStep();
          return;


        }
        stepGuide = item.linkedStepGuideId;

      } else {
        deviceTypeLong = "2";

        if (item.linkedStepGuideIdMobile <= 0) {
          // No guide linked — just mark done manually
          this.completeStep();
          return;


        }
        stepGuide = item.linkedStepGuideIdMobile;

      }

    } else if (deviceTypeLong == "3") {

      if (item.linkedStepGuideIdMobile <= 0) {
        // No guide linked — just mark done manually
        this.completeStep();
        return;


      }
      stepGuide = item.linkedStepGuideIdMobile;

    }

    this.patch({ loading: true, error: null });

    this.stepGuideService.getStepGuideById(stepGuide).subscribe({
      next: (guide: any) => {
        this.patch({ loading: false });
        const steps: GuideStep[] = (guide.stepGuideSteps || [])
          .sort((a: any, b: any) => a.stepOrder - b.stepOrder)
          .map((s: any): GuideStep => ({
            id: s.stepKey,
            text: s.text,
            route: s.route,
            action: s.action,
            overlayContext: s.overlayContext,
            manual: s.manual === 1,
            guideId: s.guideId ? s.guideId.toString() : null,
            guideKey: s.guideKey ? s.guideKey.toString() : null,
            selector: s.selector,
            matchIndex: s.matchIndex,
            description: s.stepSescription,
            imageUrl: s.imageUrl,
            imageLinkUrl: s.imageLinkUrl,
            customfield1: s.customfield1,
            customfield3: s.customfield3,
            videoLink: s.videoLink,
          }));

        // Mark item IN_PROGRESS in backend + UI
        if (item.status !== 'IN_PROGRESS' && item.status !== 'COMPLETED') {
          this.mutateItemStatus(item, 'IN_PROGRESS');
          if (!item.startedAt) { item.startedAt = new Date().toISOString(); }
          this.tourApi.markInProgress(item.stateId).subscribe();
        }

        // Close the tour overlay so the guide has full screen real estate
        this.patch({ active: false });
        document.body.classList.remove('tour-active');

        // Start the step guide
        if (steps) {
          (steps as any).deviceType = guide.deviceType;
        }
        this.guideService.start(steps, [], guide.videoLink);

        // When the guide finishes, complete the item and refresh tour state
        this.guideService.finished$.pipe(take(1)).subscribe(() => {
          this.onGuideFinished(item);
        });
      },
      error: () => {
        this.patch({ loading: false });
        // Fallback: just mark done if guide fails to load
        this.completeStep();
      },
    });
  }

  private onGuideFinished(item: TourItemStateDto): void {
    // Update local state immediately so UI reflects COMPLETED without waiting
    this.mutateItemStatus(item, 'COMPLETED');
    item.completedAt = new Date().toISOString();
    this.refreshSummary();

    // Clear stored step index so the next call won't land on the just-completed step
    sessionStorage.removeItem(STORAGE_KEY);

    // Chain: wait for markCompleted to persist on the backend BEFORE fetching
    // fresh state, so startTour never returns stale IN_PROGRESS for this item.
    this.tourApi.markCompleted(item.stateId).pipe(
      catchError(() => of(null)),          // still refresh even if mark call fails
      switchMap(() => this.tourApi.startTour())
    ).subscribe({
      next: (fresh) => {
        if (!fresh || !fresh.tourEnabled) { return; }
        const items = fresh.items ?? [];
        const nextIndex = this.resolveNextPendingIndex(items);
        document.body.classList.add('tour-active');
        this.patch({ active: true, data: fresh, currentStepIndex: nextIndex, error: null });
        this.afterStepChange();
      },
    });
  }

  /** Finds the first NOT_STARTED or IN_PROGRESS step. Falls back to 0. */
  private resolveNextPendingIndex(items: TourItemStateDto[]): number {
    const inProgressIdx = items.findIndex(i => i.status === 'IN_PROGRESS');
    if (inProgressIdx >= 0) { return inProgressIdx; }
    const notStartedIdx = items.findIndex(i => i.status === 'NOT_STARTED');
    return notStartedIdx >= 0 ? notStartedIdx : 0;
  }

  /** Close the tour overlay without completing it. */
  exitTour(): void {
    document.body.classList.remove('tour-active');
    sessionStorage.removeItem(STORAGE_KEY);
    this.clearAnchorHighlights();
    this.patch({ active: false });
  }

  /** Reset the current step back to NOT_STARTED so the user can redo it. */
  restartCurrentStep(): void {
    const item = this.currentItem;
    if (!item) {
      return;
    }
    item.startedAt = undefined;
    item.completedAt = undefined;
    this.mutateItemStatus(item, 'NOT_STARTED');
    this.refreshSummary();
  }

  /** Reset every step to NOT_STARTED and return to the first step. */
  restartTour(): void {
    const items = this._state$.value.data?.items ?? [];
    items.forEach(item => {
      item.startedAt = undefined;
      item.completedAt = undefined;
      item.status = 'NOT_STARTED';
    });
    sessionStorage.removeItem(STORAGE_KEY);
    this.patch({ currentStepIndex: 0 });
    this.refreshSummary();
    this.afterStepChange();
  }

  /** Re-open the overlay if tour data is already loaded (resume). */
  resumeTour(): void {
    if (this._state$.value.data) {
      document.body.classList.add('tour-active');
      this.patch({ active: true });
      this.afterStepChange();
    } else {
      this.startTour();
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────────────

  private get currentItem(): TourItemStateDto | null {
    const s = this._state$.value;
    return s.data?.items?.[s.currentStepIndex] ?? null;
  }

  /** Called every time the active step changes. Marks it IN_PROGRESS and scrolls to its anchor. */
  private afterStepChange(): void {
    this.markCurrentInProgress();
    this.scrollToCurrentAnchor();
  }

  private markCurrentInProgress(): void {
    const item = this.currentItem;
    if (!item || item.status === 'COMPLETED' || item.status === 'IN_PROGRESS') {
      return;
    }
    this.mutateItemStatus(item, 'IN_PROGRESS');
    if (!item.startedAt) {
      item.startedAt = new Date().toISOString();
    }
    this.tourApi.markInProgress(item.stateId).subscribe();
    // Emit updated state so the UI re-renders
    this._state$.next({ ...this._state$.value });
  }

  private scrollToCurrentAnchor(): void {
    const item = this.currentItem;
    if (!item?.itemCode) {
      return;
    }
    const anchor = this.anchors.get(item.itemCode);
    if (!anchor) {
      return;
    }
    this.clearAnchorHighlights();
    const el = anchor.nativeElement as HTMLElement;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('tour-anchor--active');
  }

  private clearAnchorHighlights(): void {
    this.anchors.forEach(ref => {
      (ref.nativeElement as HTMLElement).classList.remove('tour-anchor--active');
    });
  }

  private mutateItemStatus(item: TourItemStateDto, status: TourItemStateDto['status']): void {
    item.status = status;
    // Trigger change detection by emitting a shallow copy
    this._state$.next({ ...this._state$.value });
  }

  private refreshSummary(): void {
    const state = this._state$.value;
    if (!state.data?.checklistSummary || !state.data.items) {
      return;
    }
    const items = state.data.items;
    const total = items.length;
    const completed = items.filter(i => i.status === 'COMPLETED').length;
    const inProgress = items.filter(i => i.status === 'IN_PROGRESS').length;
    const pending = items.filter(i => i.status === 'NOT_STARTED').length;
    state.data.checklistSummary = {
      ...state.data.checklistSummary,
      completedItems: completed,
      inProgressItems: inProgress,
      pendingItems: pending,
      completionPercentage: total > 0 ? Math.round((completed / total) * 1000) / 10 : 0,
    };
    this._state$.next({ ...state });
  }

  private resolveStartIndex(items: TourItemStateDto[]): number {
    // 1. Restore last viewed step from sessionStorage
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const idx = parseInt(stored, 10);
      if (!isNaN(idx) && idx >= 0 && idx < items.length) {
        return idx;
      }
    }
    // 2. Resume from first IN_PROGRESS step
    const inProgressIdx = items.findIndex(i => i.status === 'IN_PROGRESS');
    if (inProgressIdx >= 0) {
      return inProgressIdx;
    }
    // 3. Start at first NOT_STARTED step
    const notStartedIdx = items.findIndex(i => i.status === 'NOT_STARTED');
    return notStartedIdx >= 0 ? notStartedIdx : 0;
  }

  private patch(changes: Partial<TourState>): void {
    this._state$.next({ ...this._state$.value, ...changes });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Configured-tour player
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Play a tour that was saved via the Tour Builder admin page.
   * Loads per-step checklist state from the backend, merges with the
   * configured step list, then opens the overlay at the correct resume point.
   */
  playConfiguredTour(config: TourMaster): void {
    const current = this._state$.value;
    if (current.active || current.loading) {
      return;
    }

    if (!config.tourId || !config.steps?.length) {
      this.patch({ error: 'Tour configuration is incomplete.' });
      return;
    }

    this.patch({ loading: true, error: null });

    this.tourApi.loadConfiguredTourState(config.tourId).subscribe({
      next: (res) => {
        const states = res.states ?? [];
        const items = this.buildTourItems(config.steps!, states);
        const startIndex = this.resolveStartIndex(items);

        const response: TourResponse = {
          planId: config.planId as number ?? 0,
          tourEnabled: true,
          items,
          checklistSummary: this.buildSummary(items),
        };

        document.body.classList.add('tour-active');
        this.patch({
          loading: false,
          active: true,
          data: response,
          currentStepIndex: startIndex,
          error: null,
        });

        // Navigate to the tour's starting route if configured
        if (config.routeUrl) {
          this.router.navigateByUrl(config.routeUrl);
        }

        this.afterStepChange();
      },
      error: (err) => {
        this.patch({
          loading: false,
          error: err?.error?.statusMessage || 'Failed to load tour state.',
        });
      },
    });
  }

  /**
   * Map TourStepConfig[] + backend states → TourItemStateDto[].
   * stateId is set to the checklist_item_state.id so that existing
   * markInProgress / markCompleted / markSkipped calls work without change.
   */
  private buildTourItems(
    steps: TourStepConfig[],
    states: ConfiguredStepState[],
  ): TourItemStateDto[] {
    const sorted = [...steps].sort((a, b) => a.stepNumber - b.stepNumber);
    return sorted.map((step): TourItemStateDto => {
      const state = states.find(s => s.stepGuideId === step.linkedStepGuideId);
      return {
        stateId: state?.id ?? 0,
        templateItemId: step.linkedStepGuideId ?? 0,
        itemCode: step.targetSelector ?? '',
        itemTitle: step.stepTitle,
        itemDescription: step.stepDescription,
        routePath: step.routePath,
        sortOrder: step.stepNumber,
        status: state?.status ?? 'NOT_STARTED',
        startedAt: state?.startedAt,
        completedAt: state?.completedAt,
      };
    });
  }

  private buildSummary(items: TourItemStateDto[]) {
    const total = items.length;
    const completed = items.filter(i => i.status === 'COMPLETED').length;
    const inProgress = items.filter(i => i.status === 'IN_PROGRESS').length;
    const pending = items.filter(i => i.status === 'NOT_STARTED').length;
    return {
      totalItems: total,
      completedItems: completed,
      inProgressItems: inProgress,
      pendingItems: pending,
      completionPercentage: total > 0 ? Math.round((completed / total) * 1000) / 10 : 0,
    };
  }
}
